import { persistence } from 'services/persistence';

export type ThemeId = 'default' | 'sunset' | 'nature' | 'midnight' | 'nordic-health';
export type ThemeMode = 'light' | 'dark';

export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  description: string;
  preview: string[];
  cssVars: Record<string, string>;
  darkCssVars?: Record<string, string>;
}

export const DEFAULT_THEME_ID: ThemeId = 'default';

const withCssVars = (vars: Record<string, string>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(vars).map(([key, value]) => [
      key.startsWith('--') ? key : `--${key}`,
      value
    ])
  );
};

const theme = (definition: Omit<ThemeDefinition, 'cssVars' | 'darkCssVars'> & { cssVars: Record<string, string>; darkCssVars?: Record<string, string> }): ThemeDefinition => ({
  ...definition,
  cssVars: withCssVars(definition.cssVars),
  ...(definition.darkCssVars ? { darkCssVars: withCssVars(definition.darkCssVars) } : {}),
});

export const THEMES: Record<ThemeId, ThemeDefinition> = {
  default: theme({
    id: 'default',
    name: 'EIR Serenity',
    description: 'Warm cream and calming teals with refined golden accents.',
    preview: ['#1a2f35', '#1e5f6d', '#faf5ec', '#e8a830'],
    cssVars: {
      '--color-background': '#f2f6f5',
      '--color-background-soft': '#e9efed',
      '--color-text': '#1a2f35',
      '--color-text-strong': '#0f2028',
      '--color-text-muted': '#4a7c8a',
      '--border-subtle': '#e2ddd4',
      '--border-strong': '#e8a830',
      '--surface-card': '#ffffff',
      '--surface-card-hover': '#eef5f3',
      '--surface-elevated': '#ffffff',
      '--surface-elevated-border': '#e2ddd4',
      '--surface-shadow': '0 20px 45px -28px rgba(26, 47, 53, 0.25)',
      '--header-gradient-start': '#1e5f6d',
      '--header-gradient-end': '#0d5070',
      '--header-text-color': '#ffffff',
      '--header-button-bg': 'rgba(255, 255, 255, 0.15)',
      '--header-button-hover-bg': 'rgba(255, 255, 255, 0.25)',
      '--header-button-border': 'rgba(255, 255, 255, 0.3)',
      '--header-button-hover-border': 'rgba(255, 255, 255, 0.45)',
      '--header-button-text': '#ffffff',
      '--button-neutral-bg': '#ffffff',
      '--button-neutral-text': '#1a2f35',
      '--button-neutral-border': '#d4cec5',
      '--button-neutral-hover-bg': '#f3ede3',
      '--button-primary-bg': 'rgba(255, 255, 255, 0.2)',
      '--button-primary-text': '#ffffff',
      '--button-primary-border': 'rgba(255, 255, 255, 0.3)',
      '--button-primary-hover-bg': 'rgba(255, 255, 255, 0.3)',
      '--button-secondary-bg': '#1e5f6d',
      '--button-secondary-text': '#ffffff',
      '--button-secondary-hover-bg': '#0d5070',
      '--button-accent-bg': 'linear-gradient(135deg, #1e5f6d 0%, #0d5070 100%)',
      '--button-accent-hover-bg': 'linear-gradient(135deg, #0d5070 0%, #1a2f35 100%)',
      '--button-accent-text': '#ffffff',
      '--button-accent-shadow': '0 16px 30px -20px rgba(30, 95, 109, 0.65)',
      '--button-accent-hover-shadow': '0 18px 36px -18px rgba(13, 80, 112, 0.6)',
      '--button-floating-bg': '#1a2f35',
      '--button-floating-hover-bg': '#0d5070',
      '--button-floating-text': '#ffffff',
      '--button-floating-shadow': '0 12px 26px -12px rgba(26, 47, 53, 0.4)',
      '--button-floating-hover-shadow': '0 14px 30px -12px rgba(13, 80, 112, 0.5)',
      '--input-bg': '#ffffff',
      '--input-border': '#d4cec5',
      '--input-text': '#1a2f35',
      '--input-placeholder': '#8a9e96',
      '--input-focus-border': '#1e5f6d',
      '--focus-ring': 'rgba(30, 95, 109, 0.15)',
      '--sidebar-background': '160 19% 96%',
      '--sidebar-foreground': '192 35% 15%',
      '--sidebar-accent': '158 16% 93%',
      '--sidebar-accent-foreground': '192 35% 15%',
      '--sidebar-border': '160 12% 87%',
      '--sidebar-ring': '192 64% 27%',
      '--tag-default-bg': 'rgba(30, 95, 109, 0.12)',
      '--tag-default-text': '#1e5f6d',
      '--chat-user-bg': '#e3eeeb',
      '--chat-user-text': '#0f2028',
      '--chat-assistant-bg': 'transparent',
      '--chat-assistant-text': '#0f2028',
      '--chat-area-bg': '#ffffff',
      '--composer-bg': '#ffffff',
      '--composer-border': '#e2ddd4',
      '--composer-shadow': '0 2px 12px -2px rgba(26, 47, 53, 0.08)',
      '--composer-focus-shadow': '0 2px 20px -2px rgba(30, 95, 109, 0.12)',
      '--send-button-bg': '#1e5f6d',
      '--send-button-hover-bg': '#0d5070'
    },
    darkCssVars: {
      '--color-background': '#141e22',
      '--color-background-soft': '#1a2930',
      '--color-text': '#e4ded5',
      '--color-text-strong': '#f5f0e8',
      '--color-text-muted': '#7fb3bf',
      '--border-subtle': '#263840',
      '--border-strong': '#d4952a',
      '--surface-card': '#1a2930',
      '--surface-card-hover': '#22353e',
      '--surface-elevated': '#1e2e36',
      '--surface-elevated-border': '#2e4450',
      '--surface-shadow': '0 20px 45px -28px rgba(0, 0, 0, 0.5)',
      '--header-gradient-start': '#1e5f6d',
      '--header-gradient-end': '#0d5070',
      '--header-text-color': '#f5f0e8',
      '--header-button-bg': 'rgba(245, 240, 232, 0.1)',
      '--header-button-hover-bg': 'rgba(245, 240, 232, 0.2)',
      '--header-button-border': 'rgba(245, 240, 232, 0.15)',
      '--header-button-hover-border': 'rgba(245, 240, 232, 0.3)',
      '--header-button-text': '#f5f0e8',
      '--button-neutral-bg': '#1a2930',
      '--button-neutral-text': '#e4ded5',
      '--button-neutral-border': '#2e4450',
      '--button-neutral-hover-bg': '#22353e',
      '--button-primary-bg': 'rgba(245, 240, 232, 0.15)',
      '--button-primary-text': '#f5f0e8',
      '--button-primary-border': 'rgba(245, 240, 232, 0.2)',
      '--button-primary-hover-bg': 'rgba(245, 240, 232, 0.25)',
      '--button-secondary-bg': '#1e5f6d',
      '--button-secondary-text': '#f5f0e8',
      '--button-secondary-hover-bg': '#2a7a8a',
      '--button-accent-bg': 'linear-gradient(135deg, #1e5f6d 0%, #0d5070 100%)',
      '--button-accent-hover-bg': 'linear-gradient(135deg, #2a7a8a 0%, #1e5f6d 100%)',
      '--button-accent-text': '#f5f0e8',
      '--button-accent-shadow': '0 16px 30px -20px rgba(30, 95, 109, 0.5)',
      '--button-accent-hover-shadow': '0 18px 36px -18px rgba(42, 122, 138, 0.45)',
      '--button-floating-bg': '#1e5f6d',
      '--button-floating-hover-bg': '#2a7a8a',
      '--button-floating-text': '#f5f0e8',
      '--button-floating-shadow': '0 12px 26px -12px rgba(0, 0, 0, 0.5)',
      '--button-floating-hover-shadow': '0 14px 30px -12px rgba(30, 95, 109, 0.4)',
      '--input-bg': '#1a2930',
      '--input-border': '#2e4450',
      '--input-text': '#f5f0e8',
      '--input-placeholder': '#6a8f9a',
      '--input-focus-border': '#2a7a8a',
      '--focus-ring': 'rgba(42, 122, 138, 0.25)',
      '--sidebar-background': '195 25% 11%',
      '--sidebar-foreground': '35 20% 88%',
      '--sidebar-accent': '195 25% 16%',
      '--sidebar-accent-foreground': '35 20% 88%',
      '--sidebar-border': '195 20% 20%',
      '--sidebar-ring': '192 64% 35%',
      '--tag-default-bg': 'rgba(42, 122, 138, 0.2)',
      '--tag-default-text': '#7fb3bf',
      '--chat-user-bg': '#1f3640',
      '--chat-user-text': '#f5f0e8',
      '--chat-assistant-bg': 'transparent',
      '--chat-assistant-text': '#e4ded5',
      '--chat-area-bg': '#1a2930',
      '--composer-bg': '#1a2930',
      '--composer-border': '#2e4450',
      '--composer-shadow': '0 2px 12px -2px rgba(0, 0, 0, 0.3)',
      '--composer-focus-shadow': '0 2px 20px -2px rgba(42, 122, 138, 0.25)',
      '--send-button-bg': '#1e5f6d',
      '--send-button-hover-bg': '#2a7a8a'
    }
  }),
  sunset: theme({
    id: 'sunset',
    name: 'Golden Hour',
    description: 'Warm yellow and blush pink hues inspired by sunlit skies.',
    preview: ['#fbbf24', '#fb7185', '#fff7ed'],
    cssVars: {
      '--color-background': '#fff7ed',
      '--color-background-soft': '#fef3c7',
      '--color-text': '#422006',
      '--color-text-strong': '#1f2937',
      '--color-text-muted': '#92400e',
      '--border-subtle': '#fed7aa',
      '--border-strong': '#fbbf24',
      '--surface-card': '#fff4e6',
      '--surface-card-hover': '#ffe3e0',
      '--surface-elevated': '#fff8ed',
      '--surface-elevated-border': '#fed7aa',
      '--surface-shadow': '0 24px 48px -28px rgba(249, 115, 22, 0.35)',
      '--header-gradient-start': '#f97316',
      '--header-gradient-end': '#fb7185',
      '--header-text-color': '#fffaf3',
      '--header-button-bg': 'rgba(255, 255, 255, 0.18)',
      '--header-button-hover-bg': 'rgba(255, 255, 255, 0.32)',
      '--header-button-border': 'rgba(255, 255, 255, 0.25)',
      '--header-button-hover-border': 'rgba(255, 255, 255, 0.4)',
      '--header-button-text': '#fffaf3',
      '--button-neutral-bg': '#fff4e6',
      '--button-neutral-text': '#78350f',
      '--button-neutral-border': '#fcd34d',
      '--button-neutral-hover-bg': '#ffe8d8',
      '--button-primary-bg': 'rgba(255, 255, 255, 0.2)',
      '--button-primary-text': '#fffaf3',
      '--button-primary-border': 'rgba(255, 255, 255, 0.25)',
      '--button-primary-hover-bg': 'rgba(255, 255, 255, 0.32)',
      '--button-secondary-bg': '#f97316',
      '--button-secondary-text': '#fffaf3',
      '--button-secondary-hover-bg': '#ea580c',
      '--button-accent-bg': 'linear-gradient(135deg, #facc15 0%, #f472b6 100%)',
      '--button-accent-hover-bg': 'linear-gradient(135deg, #eab308 0%, #ec4899 100%)',
      '--button-accent-text': '#422006',
      '--button-accent-shadow': '0 18px 34px -22px rgba(249, 115, 22, 0.45)',
      '--button-accent-hover-shadow': '0 20px 40px -22px rgba(236, 72, 153, 0.45)',
      '--button-floating-bg': '#be123c',
      '--button-floating-hover-bg': '#9f1239',
      '--button-floating-text': '#fffaf3',
      '--button-floating-shadow': '0 14px 32px -20px rgba(244, 114, 182, 0.45)',
      '--button-floating-hover-shadow': '0 18px 40px -20px rgba(249, 115, 22, 0.45)',
      '--input-bg': '#fffaf3',
      '--input-border': '#fcd34d',
      '--input-text': '#422006',
      '--input-placeholder': '#a16207',
      '--input-focus-border': '#f97316',
      '--focus-ring': 'rgba(249, 115, 22, 0.2)',
      '--sidebar-background': '33 100% 96%',
      '--sidebar-foreground': '15 72% 17%',
      '--sidebar-accent': '37 91% 88%',
      '--sidebar-accent-foreground': '15 72% 17%',
      '--sidebar-border': '27 87% 81%',
      '--sidebar-ring': '25 95% 53%',
      '--tag-default-bg': 'rgba(251, 191, 36, 0.22)',
      '--tag-default-text': '#92400e',
      '--chat-user-bg': '#fde8d0',
      '--chat-user-text': '#422006',
      '--chat-assistant-bg': 'transparent',
      '--chat-assistant-text': '#422006',
      '--chat-area-bg': '#fff8ed',
      '--composer-bg': '#fffaf3',
      '--composer-border': '#fcd34d',
      '--composer-shadow': '0 2px 12px -2px rgba(249, 115, 22, 0.15)',
      '--composer-focus-shadow': '0 2px 20px -2px rgba(249, 115, 22, 0.2)',
      '--send-button-bg': '#f97316',
      '--send-button-hover-bg': '#ea580c'
    }
  }),
  nature: theme({
    id: 'nature',
    name: 'Forest Bathing',
    description: 'Lush, dewy greens with tranquil neutrals.',
    preview: ['#065f46', '#22c55e', '#f0fdf4'],
    cssVars: {
      '--color-background': '#f0fdf4',
      '--color-background-soft': '#dcfce7',
      '--color-text': '#064e3b',
      '--color-text-strong': '#022c22',
      '--color-text-muted': '#0f766e',
      '--border-subtle': '#bbf7d0',
      '--border-strong': '#34d399',
      '--surface-card': '#ffffff',
      '--surface-card-hover': '#ecfdf5',
      '--surface-elevated': '#f0fdf4',
      '--surface-elevated-border': '#bbf7d0',
      '--surface-shadow': '0 24px 50px -30px rgba(22, 163, 74, 0.35)',
      '--header-gradient-start': '#0f766e',
      '--header-gradient-end': '#22c55e',
      '--header-text-color': '#f0fdf4',
      '--header-button-bg': 'rgba(255, 255, 255, 0.2)',
      '--header-button-hover-bg': 'rgba(255, 255, 255, 0.32)',
      '--header-button-border': 'rgba(255, 255, 255, 0.3)',
      '--header-button-hover-border': 'rgba(255, 255, 255, 0.45)',
      '--header-button-text': '#f0fdf4',
      '--button-neutral-bg': '#ecfdf5',
      '--button-neutral-text': '#064e3b',
      '--button-neutral-border': '#86efac',
      '--button-neutral-hover-bg': '#d1fae5',
      '--button-primary-bg': 'rgba(255, 255, 255, 0.2)',
      '--button-primary-text': '#f0fdf4',
      '--button-primary-border': 'rgba(255, 255, 255, 0.25)',
      '--button-primary-hover-bg': 'rgba(255, 255, 255, 0.32)',
      '--button-secondary-bg': '#0f766e',
      '--button-secondary-text': '#ecfdf5',
      '--button-secondary-hover-bg': '#0d9488',
      '--button-accent-bg': 'linear-gradient(135deg, #22c55e 0%, #14b8a6 100%)',
      '--button-accent-hover-bg': 'linear-gradient(135deg, #16a34a 0%, #0d9488 100%)',
      '--button-accent-text': '#022c22',
      '--button-accent-shadow': '0 18px 34px -22px rgba(45, 212, 191, 0.45)',
      '--button-accent-hover-shadow': '0 20px 42px -22px rgba(14, 116, 144, 0.45)',
      '--button-floating-bg': '#047857',
      '--button-floating-hover-bg': '#065f46',
      '--button-floating-text': '#ecfdf5',
      '--button-floating-shadow': '0 14px 32px -20px rgba(34, 197, 94, 0.45)',
      '--button-floating-hover-shadow': '0 18px 40px -24px rgba(14, 116, 144, 0.4)',
      '--input-bg': '#ffffff',
      '--input-border': '#86efac',
      '--input-text': '#022c22',
      '--input-placeholder': '#0d9488',
      '--input-focus-border': '#0f766e',
      '--focus-ring': 'rgba(34, 197, 94, 0.2)',
      '--sidebar-background': '152 81% 96%',
      '--sidebar-foreground': '164 77% 20%',
      '--sidebar-accent': '149 80% 90%',
      '--sidebar-accent-foreground': '164 77% 20%',
      '--sidebar-border': '142 77% 73%',
      '--sidebar-ring': '160 84% 39%',
      '--tag-default-bg': 'rgba(34, 197, 94, 0.2)',
      '--tag-default-text': '#0f766e',
      '--chat-user-bg': '#d1f5e0',
      '--chat-user-text': '#022c22',
      '--chat-assistant-bg': 'transparent',
      '--chat-assistant-text': '#064e3b',
      '--chat-area-bg': '#f0fdf4',
      '--composer-bg': '#ffffff',
      '--composer-border': '#86efac',
      '--composer-shadow': '0 2px 12px -2px rgba(22, 163, 74, 0.15)',
      '--composer-focus-shadow': '0 2px 20px -2px rgba(22, 163, 74, 0.2)',
      '--send-button-bg': '#0f766e',
      '--send-button-hover-bg': '#0d9488'
    }
  }),
  midnight: theme({
    id: 'midnight',
    name: 'Midnight Aurora',
    description: 'Deep cosmic blues with shimmering accents for night owls.',
    preview: ['#0f172a', '#4c1d95', '#111827'],
    cssVars: {
      '--color-background': '#0b1120',
      '--color-background-soft': '#111827',
      '--color-text': '#e2e8f0',
      '--color-text-strong': '#f8fafc',
      '--color-text-muted': '#cbd5f5',
      '--border-subtle': '#1f2937',
      '--border-strong': '#312e81',
      '--surface-card': '#111827',
      '--surface-card-hover': '#1e293b',
      '--surface-elevated': '#111827',
      '--surface-elevated-border': '#1f2937',
      '--surface-shadow': '0 28px 60px -30px rgba(30, 64, 175, 0.45)',
      '--header-gradient-start': '#312e81',
      '--header-gradient-end': '#4c1d95',
      '--header-text-color': '#e0e7ff',
      '--header-button-bg': 'rgba(224, 231, 255, 0.12)',
      '--header-button-hover-bg': 'rgba(224, 231, 255, 0.22)',
      '--header-button-border': 'rgba(224, 231, 255, 0.18)',
      '--header-button-hover-border': 'rgba(224, 231, 255, 0.32)',
      '--header-button-text': '#e0e7ff',
      '--button-neutral-bg': '#111827',
      '--button-neutral-text': '#e2e8f0',
      '--button-neutral-border': '#334155',
      '--button-neutral-hover-bg': '#1f2937',
      '--button-primary-bg': 'rgba(224, 231, 255, 0.18)',
      '--button-primary-text': '#e0e7ff',
      '--button-primary-border': 'rgba(224, 231, 255, 0.2)',
      '--button-primary-hover-bg': 'rgba(224, 231, 255, 0.28)',
      '--button-secondary-bg': '#4338ca',
      '--button-secondary-text': '#f5f3ff',
      '--button-secondary-hover-bg': '#3730a3',
      '--button-accent-bg': 'linear-gradient(135deg, #38bdf8 0%, #6366f1 100%)',
      '--button-accent-hover-bg': 'linear-gradient(135deg, #0ea5e9 0%, #4338ca 100%)',
      '--button-accent-text': '#f8fafc',
      '--button-accent-shadow': '0 20px 40px -26px rgba(59, 130, 246, 0.5)',
      '--button-accent-hover-shadow': '0 24px 46px -26px rgba(99, 102, 241, 0.55)',
      '--button-floating-bg': '#1d4ed8',
      '--button-floating-hover-bg': '#1e40af',
      '--button-floating-text': '#f8fafc',
      '--button-floating-shadow': '0 16px 38px -26px rgba(59, 130, 246, 0.55)',
      '--button-floating-hover-shadow': '0 22px 42px -26px rgba(79, 70, 229, 0.55)',
      '--input-bg': '#0f172a',
      '--input-border': '#1f2937',
      '--input-text': '#f8fafc',
      '--input-placeholder': '#94a3b8',
      '--input-focus-border': '#4f46e5',
      '--focus-ring': 'rgba(99, 102, 241, 0.25)',
      '--sidebar-background': '222 47% 11%',
      '--sidebar-foreground': '215 28% 87%',
      '--sidebar-accent': '217 33% 17%',
      '--sidebar-accent-foreground': '215 28% 87%',
      '--sidebar-border': '217 19% 17%',
      '--sidebar-ring': '217.2 91.2% 59.8%',
      '--tag-default-bg': 'rgba(99, 102, 241, 0.22)',
      '--tag-default-text': '#cbd5f5',
      '--chat-user-bg': '#1e293b',
      '--chat-user-text': '#f8fafc',
      '--chat-assistant-bg': 'transparent',
      '--chat-assistant-text': '#e2e8f0',
      '--chat-area-bg': '#111827',
      '--composer-bg': '#0f172a',
      '--composer-border': '#1f2937',
      '--composer-shadow': '0 2px 12px -2px rgba(0, 0, 0, 0.4)',
      '--composer-focus-shadow': '0 2px 20px -2px rgba(99, 102, 241, 0.25)',
      '--send-button-bg': '#4338ca',
      '--send-button-hover-bg': '#3730a3'
    }
  }),
  'nordic-health': theme({
    id: 'nordic-health',
    name: 'Nordic Health',
    description: 'Clean, formal healthcare design with white backgrounds and professional typography.',
    preview: ['#ffffff', '#0f172a', '#475569'],
    cssVars: {
      '--color-background': '#ffffff',
      '--color-background-soft': '#f8fafc',
      '--color-text': '#0f172a',
      '--color-text-strong': '#000000',
      '--color-text-muted': '#475569',
      '--border-subtle': '#e2e8f0',
      '--border-strong': '#cbd5e1',
      '--surface-card': '#ffffff',
      '--surface-card-hover': '#f8fafc',
      '--surface-elevated': '#ffffff',
      '--surface-elevated-border': '#e2e8f0',
      '--surface-shadow': '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      '--header-gradient-start': '#ffffff',
      '--header-gradient-end': '#f8fafc',
      '--header-text-color': '#0f172a',
      '--header-button-bg': 'rgba(15, 23, 42, 0.05)',
      '--header-button-hover-bg': 'rgba(15, 23, 42, 0.1)',
      '--header-button-border': 'rgba(15, 23, 42, 0.1)',
      '--header-button-hover-border': 'rgba(15, 23, 42, 0.2)',
      '--header-button-text': '#0f172a',
      '--button-neutral-bg': '#ffffff',
      '--button-neutral-text': '#0f172a',
      '--button-neutral-border': '#cbd5e1',
      '--button-neutral-hover-bg': '#f8fafc',
      '--button-primary-bg': '#0f172a',
      '--button-primary-text': '#ffffff',
      '--button-primary-border': '#0f172a',
      '--button-primary-hover-bg': '#1e293b',
      '--button-secondary-bg': '#475569',
      '--button-secondary-text': '#ffffff',
      '--button-secondary-hover-bg': '#334155',
      '--button-accent-bg': '#0f172a',
      '--button-accent-hover-bg': '#1e293b',
      '--button-accent-text': '#ffffff',
      '--button-accent-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      '--button-accent-hover-shadow': '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      '--button-floating-bg': '#0f172a',
      '--button-floating-hover-bg': '#1e293b',
      '--button-floating-text': '#ffffff',
      '--button-floating-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      '--button-floating-hover-shadow': '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      '--input-bg': '#ffffff',
      '--input-border': '#cbd5e1',
      '--input-text': '#0f172a',
      '--input-placeholder': '#94a3b8',
      '--input-focus-border': '#0f172a',
      '--focus-ring': 'rgba(15, 23, 42, 0.1)',
      '--sidebar-background': '0 0% 100%',
      '--sidebar-foreground': '222 47% 11%',
      '--sidebar-accent': '210 40% 96%',
      '--sidebar-accent-foreground': '222 47% 11%',
      '--sidebar-border': '214 32% 91%',
      '--sidebar-ring': '222 47% 11%',
      '--tag-default-bg': 'rgba(15, 23, 42, 0.1)',
      '--tag-default-text': '#0f172a',
      '--chat-user-bg': '#f1f5f9',
      '--chat-user-text': '#0f172a',
      '--chat-assistant-bg': 'transparent',
      '--chat-assistant-text': '#0f172a',
      '--chat-area-bg': '#ffffff',
      '--composer-bg': '#ffffff',
      '--composer-border': '#cbd5e1',
      '--composer-shadow': '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      '--composer-focus-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      '--send-button-bg': '#0f172a',
      '--send-button-hover-bg': '#1e293b'
    }
  })
};

export const AVAILABLE_THEMES: ThemeDefinition[] = Object.values(THEMES);

export const isValidThemeId = (value: unknown): value is ThemeId => {
  return typeof value === 'string' && value in THEMES;
};

export const DEFAULT_THEME_MODE: ThemeMode = 'light';

export const applyTheme = (themeId: ThemeId, mode: ThemeMode = 'light'): void => {
  if (typeof window === 'undefined') return;
  const theme = THEMES[themeId] ?? THEMES[DEFAULT_THEME_ID];
  const isDark = mode === 'dark' && Boolean(theme.darkCssVars);
  const vars = isDark ? theme.darkCssVars! : theme.cssVars;
  const root = document.documentElement;

  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  root.setAttribute('data-theme', theme.id);
  root.setAttribute('data-mode', isDark ? 'dark' : 'light');
  root.classList.toggle('dark', isDark);
};

export const getThemeDefinition = (themeId: ThemeId): ThemeDefinition => {
  return THEMES[themeId] ?? THEMES[DEFAULT_THEME_ID];
};

export const themeHasDarkMode = (themeId: ThemeId): boolean => {
  const theme = THEMES[themeId];
  return Boolean(theme?.darkCssVars);
};

const INTRINSICALLY_DARK_THEMES: ThemeId[] = ['midnight'];

export const isThemeDark = (themeId: ThemeId, mode: ThemeMode): boolean => {
  if (INTRINSICALLY_DARK_THEMES.includes(themeId)) return true;
  return mode === 'dark' && themeHasDarkMode(themeId);
};

export const getStoredThemeId = (): ThemeId => {
  const parsed = persistence.getJSON<{theme?: string} | null>('eir-user-profile', null);
  if (parsed && isValidThemeId(parsed?.theme)) return parsed.theme;
  return DEFAULT_THEME_ID;
};

export const getStoredThemeMode = (): ThemeMode => {
  const parsed = persistence.getJSON<{themeMode?: string} | null>('eir-user-profile', null);
  if (parsed?.themeMode === 'dark' || parsed?.themeMode === 'light') {
    return parsed.themeMode;
  }
  return DEFAULT_THEME_MODE;
};

export const setStoredThemeMode = (mode: ThemeMode): void => {
  const parsed = persistence.getJSON<Record<string, unknown>>('eir-user-profile', {});
  parsed.themeMode = mode;
  persistence.setJSON('eir-user-profile', parsed);
};
